// src/routes/Home/HomeComponents/PlanCard.js

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  LinearProgress,
  Grid,
  Paper,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { bg, border, overlay } from '../../../utils/darkModeAdapter';

/**
 * Props:
 *  - planData: {
 *      difficulty: string,
 *      recruitsPeriod: string,
 *      recommendedHiresAndApplicants: {
 *        recommendedHires: string,
 *        requiredApplicants: string
 *      },
 *      budgetDistribution: {
 *        media: number,
 *        agency: number,
 *        others: number
 *      }
 *    }
 * 
 *  planData が null もしくは undefined の場合はフォールバック表示を行う。
 */

function PlanCard({ planData, hideTitle = false, budgetYen }) {
  const theme = useTheme();

  // ① planData が無い => フォールバック表示
  if (!planData) {
    return (
      <Card
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          ...commonCardStylesDark(theme),
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            <BusinessCenterIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            採用計画が未確定です
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            現在、採用計画が作成されていません。
            <br />
            採用計画を策定してください。
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: overlay(theme).light,
              borderRadius: 2,
              border: `1px dashed ${overlay(theme).medium}`
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              採用計画では、募集人数・予算・採用期間などの基本方針を設定します。
              採用計画を立てると、媒体選定などの次のステップに進むことができます。
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ② planData が存在する場合の表示
  const {
    difficulty = '',
    recruitsPeriod = '',
    recommendedHiresAndApplicants = {},
    budgetDistribution = {},
    isDecided = true, // デフォルトをtrueに変更
  } = planData;

  const {
    recommendedHires = '',
    requiredApplicants = '',
  } = recommendedHiresAndApplicants;

  const {
    media = 0,
    agency = 0,
    others = 0,
  } = budgetDistribution;

  // 予算を数値として扱い、円単位から万円単位に変換
  const totalBudgetYen = Number(budgetYen || 0);
  const totalBudgetMan = Math.floor(totalBudgetYen / 10000); // 円から万円に変換
  
  // 予算配分の計算（パーセンテージから実際の金額へ）
  const mediaPercent = Number(media || 0);
  const agencyPercent = Number(agency || 0);
  const othersPercent = Number(others || 0);
  
  // 各予算を計算（万円単位）
  const mediaAmount = Math.floor(totalBudgetMan * (mediaPercent / 100));
  const agencyAmount = Math.floor(totalBudgetMan * (agencyPercent / 100));
  const othersAmount = Math.floor(totalBudgetMan * (othersPercent / 100));
  
  // 合計予算（万円単位）
  const totalBudget = totalBudgetMan;

  // 決定済みかどうかでタイトルとデザインを変える
  const cardTitle = isDecided ? "確定した採用計画" : "最新の採用計画案";
  const cardColor = '#ff9800';
  const cardHeaderColor = '#ff9800';

  // 難易度に応じた色とアイコンを設定
  const getDifficultyColor = () => {
    // DifficultyCard.jsの色定義と完全に一致させる
    if (difficulty.includes('容易')) return designTokens.difficultyColors.veryEasy;
    if (difficulty.includes('やや容易')) return designTokens.difficultyColors.easy;
    if (difficulty.includes('やや困難')) return designTokens.difficultyColors.slightlyDifficult;
    if (difficulty.includes('困難') && !difficulty.includes('非常に')) return designTokens.difficultyColors.difficult;
    if (difficulty.includes('非常に困難')) return designTokens.difficultyColors.veryDifficult;

    // 旧方式の難易度表記をサポート
    if (difficulty.includes('低') || difficulty.includes('易')) return designTokens.difficultyColors.veryEasy;
    if (difficulty.includes('中') || difficulty.includes('普通')) return designTokens.difficultyColors.slightlyDifficult;
    if (difficulty.includes('高') || difficulty.includes('難')) return designTokens.difficultyColors.difficult;

    return theme.palette.text.secondary; // デフォルトグレー
  };

  const difficultyColor = getDifficultyColor();

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        mb: 3,
        backgroundColor: '#ff98000D',
      }}
    >

      <CardContent
        sx={{
          py: 3,
          px: 3,
        }}
      >
        {!hideTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                color: cardColor,
              }}
            >
              <BusinessCenterIcon sx={{ mr: 1, fontSize: '1.3rem' }} />
              {cardTitle}
            </Typography>
            
            {isDecided && (
              <Chip
                icon={<CheckCircleIcon sx={{ color: '#fff !important' }} />}
                label="確定済み"
                size="small"
                sx={{ fontWeight: 'bold', backgroundColor: '#ff9800', color: '#fff' }}
              />
            )}
          </Box>
        )}

        {/* 採用計画が確定していない場合の注意メッセージ */}
        {!isDecided && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 244, 229, 0.7)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.warning.main,
                fontWeight: 500,
              }}
            >
              この採用計画はまだ確定していません。計画の確定には採用計画ページで決定ボタンを押してください。
            </Typography>
          </Box>
        )}

        {/* 採用難易度・採用期間 */}
        <Grid
          container
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Grid item xs={12} sm={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: overlay(theme).whiteLight,
                border: '1px solid rgba(0, 0, 0, 0.05)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%', 
                    backgroundColor: difficultyColor, 
                    display: 'inline-block',
                    mr: 1
                  }} 
                />
                採用難易度
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: difficultyColor
                }}
              >
                {difficulty}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: bg(theme).subtle,
                border: `1px solid ${border(theme).light}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}
              >
                <CalendarTodayIcon sx={{ fontSize: '0.9rem', mr: 1 }} />
                採用期間
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {recruitsPeriod}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* 募集人数 */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              color: cardColor,
            }}
          >
            <PeopleAltIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            採用人数
          </Typography>


          <Grid
            container
            spacing={2}
          >
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: bg(theme).subtle,
                  border: `1px solid ${border(theme).light}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  募集人数
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  {recommendedHires} 人
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: bg(theme).subtle,
                  border: `1px solid ${border(theme).light}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  必要応募者数
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                  {requiredApplicants} 人
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 予算配分 */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              color: cardColor,
            }}
          >
            <AccountBalanceWalletIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            予算配分
          </Typography>


          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <BudgetInfo label="媒体費" amount={mediaAmount} total={totalBudget} color={designTokens.budgetColors.media} />
            <BudgetInfo label="人材紹介" amount={agencyAmount} total={totalBudget} color={designTokens.budgetColors.agency} />
            <BudgetInfo label="その他" amount={othersAmount} total={totalBudget} color={designTokens.budgetColors.others} />
          </Box>
        </Box>


        {/* 合計予算 */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: bg(theme).subtle,
            textAlign: 'center',
            border: `1px dashed ${border(theme).default}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            予算合計
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {Number(totalBudget || 0).toLocaleString()}万円
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default PlanCard;

/** 小コンポーネント: 数値や文字を表示するシンプルなUI */
function InfoItem({ label, value }) {
  return (
    <Box
      sx={{
        minWidth: 100,
        p: 1,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** 予算情報の表示コンポーネント */
function BudgetInfo({ label, amount, total, color }) {
  const theme = useTheme();
  // 割合の計算 (0-100%)
  const percentage = total > 0 ? Math.round((amount / total) * 100) : 0;

  // 金額を確実に数値にして表示
  const safeAmount = Number(amount || 0);

  return (
    <Box
      sx={{ mb: 1 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: color,
              display: 'inline-block',
              mr: 1,
            }}
          />
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {safeAmount.toLocaleString()}万円
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {percentage}%
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: border(theme).light,
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: color,
          },
        }}
      />
    </Box>
  );
}